import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import styled from 'styled-components';
import Select from 'react-dropdown-select';
import { Form, Col, InputGroup, Modal, Button, Table, Row, Spinner, Alert } from 'react-bootstrap';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import axios from 'axios';
import { garmentActions } from '../../actions/garment/garmentActions';
import { actorActions } from '../../actions/scene/actorActions';
import constants from '../../const';
import {
    CropContainer,
    PreviewContainer,
    StyledButton,
    ModernModal,
    GarmentTable,
    SortIcon,
    ActionButtonGroup,
    ActorBadge,
    ColorBadge,
    CategoryBadge,
    CompactDetails,
    DetailBadge,
    GarmentDetails,
    DetailItem,
    GarmentImagePreview,
    ImagePreviewSmall,
    ImageThumbnail,
    getContrast
} from './garment-manager.styles';
import SceneCard from '../scenes/scene-card';

const HOVER_SCENES_OPEN_DELAY_MS = 300;
const HOVER_SCENES_CLOSE_DELAY_MS = 150;

const compareGarmentValues = (a, b) => {
    const aEmpty = a === null || a === undefined || a === '';
    const bEmpty = b === null || b === undefined || b === '';
    if (aEmpty || bEmpty) return aEmpty === bEmpty ? 0 : (aEmpty ? 1 : -1);
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).toLowerCase().localeCompare(String(b).toLowerCase());
};

function GarmentManager(props) {
    const { sceneId, albumId, onGarmentTagsUpdated } = props;
    const isTaggerMode = !!(sceneId || albumId);
    const taggingId = sceneId || albumId;
    const taggingType = sceneId ? 'scene' : 'album';
    const isMobile = window.innerWidth <= constants.mobileSize;
    
    const [showGarmentList, setShowGarmentList] = useState(false);
    const [showGarmentEditor, setShowGarmentEditor] = useState(false);
    const [garmentMode, setGarmentMode] = useState("Edit");
    const [order, setOrder] = useState("asc");
    const [sortKey, setSortKey] = useState(null);
    const [filterActor, setFilterActor] = useState("");
    const [filterCategory, setFilterCategory] = useState("");
    const [filterText, setFilterText] = useState("");
    const [garmentTotal, setGarmentTotal] = useState(null);
    const [garmentImage, setGarmentImage] = useState("");
    const [showGarmentImage, setShowGarmentImage] = useState(false);
    const [selectedActorId, setSelectedActorId] = useState(props.actorId || null);
    const fileInputRef = useRef(null);

    // Scene tagger state (active when sceneId prop is provided)
    const [selectedGarments, setSelectedGarments] = useState(new Set(props.sceneGarments ? props.sceneGarments.map(g => g.id) : []));
    const [showTaggedOnly, setShowTaggedOnly] = useState(false);
    const [isSavingTags, setIsSavingTags] = useState(false);

    // Image cropping states (cropper lives inside the Add Garment editor)
    const [cropImage, setCropImage] = useState(null);
    const [cropRatio, setCropRatio] = useState(1); // 1 for 1:1, 0.75 for 4:3, 1.33 for 3:4, etc.
    const [crop, setCrop] = useState({ unit: 'px', width: 300, aspect: 1 });
    const [completedCrop, setCompletedCrop] = useState(null);
    const previewCanvasRef = useRef(null);
    const imgRef = useRef(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadPrompt, setUploadPrompt] = useState('');
    const [flatLayModel, setFlatLayModel] = useState('auto');
    const pixelRatio = window.devicePixelRatio || 1;

    const [garmentInfo, setGarmentInfo] = useState({
        name: '',
        category_id: '',
        style: '',
        pattern: '',
        straps: '',
        cut: '',
        color: '',
        texture: '',
        material: '',
        additional_attributes: '',
        description: '',
        actor_id: '',
        image_url: '',
        flatlay_url: ''
    });

    // Image history states
    const [imageHistory, setImageHistory] = useState([]);
    const [showImagePreview, setShowImagePreview] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [loadingImageHistory, setLoadingImageHistory] = useState(false);

    // Garment scenes viewer state
    const [showScenesModal, setShowScenesModal] = useState(false);
    const [scenesGarment, setScenesGarment] = useState(null);
    const [garmentScenes, setGarmentScenes] = useState([]);
    const [scenesLoading, setScenesLoading] = useState(false);

    // Hover preview of the scenes a garment is tagged to
    const [hoverScenes, setHoverScenes] = useState(null);
    const hoverScenesCacheRef = useRef(new Map());
    const hoverOpenTimerRef = useRef(null);
    const hoverCloseTimerRef = useRef(null);
    const hoverRequestIdRef = useRef(0);

    // Regenerate flat lay states
    const [flatLayPrompt, setFlatLayPrompt] = useState('');
    const [isRegeneratingFlatLay, setIsRegeneratingFlatLay] = useState(false);
    const [regenerateError, setRegenerateError] = useState(null);
    const [regenerateSuccess, setRegenerateSuccess] = useState(false);
    const [regenerateNotice, setRegenerateNotice] = useState(null);

    // Background generation queue (AI analysis and flat lay regeneration run on the server)
    const [garmentJobs, setGarmentJobs] = useState(null);
    const jobStatusRef = useRef(null);
    const handleJobFinishedRef = useRef(null);

    const fetchGarmentJobs = useCallback(async () => {
        try {
            const token = localStorage.getItem('token')?.replace('Bearer ', '') || '';
            const res = await axios.get(`${constants.CONTENT_DOMAIN}upload/garment/jobs?token=${token}`);
            setGarmentJobs(res.data.data || []);
        } catch (error) {
            console.warn('Failed to load generation queue:', error.message);
        }
    }, []);

    const hasActiveJobs = (garmentJobs || []).some(job => job.status === 'queued' || job.status === 'running');

    const refreshGarmentList = () => {
        if (selectedActorId) {
            props.getGarmentsByActorId(selectedActorId);
        } else {
            props.getAllGarments({ limit: 200, sort_by: 'id', sort_order: 'desc' });
        }
    };

    // Replaces the editor form with the saved record so fields written by the analysis job appear
    const reloadGarmentIntoEditor = (garmentId, flatLayUrl) => {
        return props.getGarmentById(garmentId)
            .then(res => {
                if (!res?.garment) return;
                setGarmentInfo(prev => String(prev.id) === String(garmentId)
                    ? { ...res.garment, flatlay_url: flatLayUrl || prev.flatlay_url || null }
                    : prev);
            })
            .catch(error => console.error('Error reloading garment after analysis:', error));
    };

    const handleJobFinished = (job) => {
        refreshGarmentList();

        const isEditorGarment = showGarmentEditor && garmentInfo.id && String(garmentInfo.id) === String(job.garmentId);
        if (!isEditorGarment) return;

        if (job.status === 'failed') {
            if (job.type === 'regenerate') {
                setRegenerateNotice(null);
                setRegenerateError(job.error || 'Flat lay generation failed');
            } else {
                setUploadError(job.error || 'Garment analysis failed');
            }
            return;
        }

        if (job.type === 'analyze') {
            reloadGarmentIntoEditor(job.garmentId, job.result?.flatLayUrl);
        }

        if (job.result?.flatLayUrl) {
            setGarmentInfo(prev => ({ ...prev, flatlay_url: job.result.flatLayUrl }));
        }
        if (job.type === 'regenerate') {
            setRegenerateNotice(null);
            setRegenerateSuccess(true);
        }
        props.getGarmentImageHistory(job.garmentId)
            .then(setImageHistory)
            .catch(error => console.error('Error refreshing image history:', error));
    };
    handleJobFinishedRef.current = handleJobFinished;

    // Load the queue while the garment manager is open
    useEffect(() => {
        if (showGarmentList || showGarmentEditor) {
            fetchGarmentJobs();
        }
    }, [showGarmentList, showGarmentEditor, fetchGarmentJobs]);

    // Poll while any job is still queued or running
    useEffect(() => {
        if (!hasActiveJobs) return undefined;
        const timer = setInterval(fetchGarmentJobs, 3000);
        return () => clearInterval(timer);
    }, [hasActiveJobs, fetchGarmentJobs]);

    // React to jobs that finished since the previous poll (the first load is not announced)
    useEffect(() => {
        if (!garmentJobs) return;
        const previous = jobStatusRef.current;
        jobStatusRef.current = new Map(garmentJobs.map(job => [job.id, job.status]));
        if (!previous) return;
        garmentJobs
            .filter(job => (job.status === 'completed' || job.status === 'failed') && previous.get(job.id) !== job.status)
            .forEach(job => handleJobFinishedRef.current(job));
    }, [garmentJobs]);

    const openScenesModal = (garment) => {
        clearTimeout(hoverOpenTimerRef.current);
        setHoverScenes(null);
        setScenesGarment(garment);
        setGarmentScenes([]);
        setScenesLoading(true);
        setShowScenesModal(true);
        props.getGarmentScenes(garment.id)
            .then(res => { setGarmentScenes(res?.scenes || []); })
            .catch(() => {})
            .finally(() => setScenesLoading(false));
    };

    const cancelHoverScenesClose = () => {
        clearTimeout(hoverCloseTimerRef.current);
    };

    const scheduleHoverScenesClose = () => {
        clearTimeout(hoverOpenTimerRef.current);
        cancelHoverScenesClose();
        hoverCloseTimerRef.current = setTimeout(() => {
            hoverRequestIdRef.current++;
            setHoverScenes(null);
        }, HOVER_SCENES_CLOSE_DELAY_MS);
    };

    const handleGarmentRowEnter = (garment) => {
        cancelHoverScenesClose();
        clearTimeout(hoverOpenTimerRef.current);
        hoverOpenTimerRef.current = setTimeout(() => {
            const requestId = ++hoverRequestIdRef.current;
            const cached = hoverScenesCacheRef.current.get(garment.id);
            if (cached) {
                setHoverScenes({ garment, scenes: cached, loading: false });
                return;
            }
            setHoverScenes({ garment, scenes: [], loading: true });
            props.getGarmentScenes(garment.id)
                .then(res => {
                    const scenes = res?.scenes || [];
                    hoverScenesCacheRef.current.set(garment.id, scenes);
                    if (requestId === hoverRequestIdRef.current) {
                        setHoverScenes({ garment, scenes, loading: false });
                    }
                })
                .catch(() => {
                    if (requestId === hoverRequestIdRef.current) {
                        setHoverScenes({ garment, scenes: [], loading: false });
                    }
                });
        }, HOVER_SCENES_OPEN_DELAY_MS);
    };

    useEffect(() => {
        if (!showGarmentList) {
            setHoverScenes(null);
        }
    }, [showGarmentList]);

    useEffect(() => () => {
        clearTimeout(hoverOpenTimerRef.current);
        clearTimeout(hoverCloseTimerRef.current);
    }, []);

    // Sync show prop to internal state (when parent controls visibility)
    useEffect(() => {
        if (props.show !== undefined) {
            setShowGarmentList(props.show);
        }
    }, [props.show]);

    // Open the Add Garment editor with an image sent from the gallery, then load it into the cropper
    useEffect(() => {
        if (!props.sourceImage) return;
        const actorId = props.actorId || selectedActorId;
        setSelectedActorId(actorId);
        setShowGarmentList(true);
        setGarmentMode("Add");
        resetGarmentForm();
        setGarmentInfo(prev => ({ ...prev, actor_id: actorId || '' }));
        setUploadError(null);
        setShowGarmentEditor(true);

        axios.get(props.sourceImage.url, { responseType: 'blob' })
            .then(res => new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(res.data);
            }))
            .then(dataUrl => {
                setCropImage(dataUrl);
                setCropRatio(1);
                setCrop({ unit: 'px', width: 300, aspect: 1 });
                setCompletedCrop(null);
            })
            .catch(() => setUploadError('Failed to load the selected gallery image'))
            .finally(() => props.onSourceImageHandled?.());
    }, [props.sourceImage]);

    // Load attributes and actors when opening Wardrobe Manager
    useEffect(() => {
        if (showGarmentList) {
            props.getGarmentAttributes();
            props.getAllActor();
        }
    }, [showGarmentList]);

    // Debounced server-side search: re-fetch when filterText, filterActor, or filterCategory changes
    useEffect(() => {
        if (!showGarmentList || selectedActorId) return;
        const timer = setTimeout(() => {
            props.getAllGarments({ search: filterText || undefined, limit: 200, sort_by: 'id', sort_order: 'desc' })
                .then(res => { if (res && res.pagination) setGarmentTotal(res.pagination.total); })
                .catch(() => {});
        }, 400);
        return () => clearTimeout(timer);
    }, [filterText, showGarmentList]);

    // Sync selected garments when sceneGarments changes (tagger mode)
    useEffect(() => {
        if (props.sceneGarments) {
            setSelectedGarments(new Set(props.sceneGarments.map(g => g.id)));
        }
    }, [props.sceneGarments]);

    const handleShow = (actorId) => {
        setSelectedActorId(actorId);
        setShowGarmentList(true);
        setSortKey(null);
        setOrder("asc");
        setFilterActor("");
        setFilterCategory("");
        setFilterText("");
        if (actorId) {
            props.getGarmentsByActorId(actorId);
        } else {
            props.getAllGarments({ limit: 200, sort_by: 'id', sort_order: 'desc' })
                .then(res => { if (res && res.pagination) setGarmentTotal(res.pagination.total); })
                .catch(() => {});
        }
        if (sceneId) {
            props.getSceneGarments(sceneId);
        }
    };

    const handleCloseGarmentList = () => {
        setShowGarmentList(false);
        setShowGarmentEditor(false);
        if (props.onClose) {
            props.onClose();
        }
    };

    const resetCropState = () => {
        setCropImage(null);
        setCropRatio(1);
        setCrop({ unit: 'px', width: 300, aspect: 1 });
        setCompletedCrop(null);
        setUploadPrompt('');
    };

    const handleCancel = () => {
        setShowGarmentEditor(false);
        resetGarmentForm();
        resetCropState();
    };

    const resetGarmentForm = () => {
        setGarmentInfo({
            name: '',
            category_id: '',
            style: '',
            pattern: '',
            straps: '',
            cut: '',
            color: '',
            texture: '',
            material: '',
            additional_attributes: '',
            description: '',
            actor_id: selectedActorId || '',
            image_url: '',
            flatlay_url: ''
        });
    };

    const handleSaveGarment = async () => {
        try {
            const data = {
                ...garmentInfo,
                actor_id: garmentInfo.actor_id || selectedActorId,
                additional_attributes: garmentInfo.additional_attributes || null
            };

            if (garmentMode === "Edit" && garmentInfo.id) {
                await props.updateGarment(garmentInfo.id, data);
            } else {
                await props.createGarment(data);
            }

            setShowGarmentEditor(false);
            resetGarmentForm();
            
            // Refresh list
            if (selectedActorId) {
                props.getGarmentsByActorId(selectedActorId);
            } else {
                props.getAllGarments({ limit: 200, sort_by: 'id', sort_order: 'desc' });
            }
        } catch (error) {
            console.error('Error saving garment:', error);
        }
    };

    const handleRegenerateFlatLay = async () => {
        if (!flatLayPrompt.trim() || !garmentInfo.id) return;

        setIsRegeneratingFlatLay(true);
        setRegenerateError(null);
        setRegenerateSuccess(false);
        setRegenerateNotice(null);

        try {
            const actorId = garmentInfo.actor_id || selectedActorId;
            const regenerationAttributes = {
                category: garmentInfo.category_name || garmentInfo.category || '',
                style: garmentInfo.style_name || garmentInfo.style || '',
                pattern: garmentInfo.pattern_name || garmentInfo.pattern || '',
                color_name: garmentInfo.color_name || garmentInfo.color || '',
                color_hex: garmentInfo.color_hex || '',
                material_texture: garmentInfo.texture_name || garmentInfo.material_name || garmentInfo.material_texture || '',
                cut_fit: garmentInfo.cut || garmentInfo.cut_fit || garmentInfo.fit_type || '',
                straps_details: garmentInfo.straps || garmentInfo.straps_details || '',
                description: garmentInfo.description || '',
                construction_type: garmentInfo.construction_type || '',
                fit_type: garmentInfo.fit_type || '',
                waistband_style: garmentInfo.waistband_style || '',
                trim_details: garmentInfo.trim_details || '',
                side_seam: garmentInfo.side_seam || '',
                pattern_detail: garmentInfo.pattern_detail || ''
            };
            const result = await props.regenerateFlatLay(
                garmentInfo.id,
                actorId,
                flatLayPrompt.trim(),
                flatLayModel !== 'auto' ? flatLayModel : null,
                regenerationAttributes
            );

            if (result.status && result.data?.jobId) {
                const position = result.data.queuePosition;
                setRegenerateNotice(position
                    ? `Queued (position ${position}). The new flat lay will appear here when it's ready.`
                    : "Generating the new flat lay now. It will appear here when it's ready.");
                setFlatLayPrompt('');
            } else {
                setRegenerateError(result.message || 'Failed to queue flat lay generation');
            }
        } catch (error) {
            setRegenerateError(error.response?.data?.message || error.message || 'Failed to regenerate flat lay');
        } finally {
            setIsRegeneratingFlatLay(false);
        }
    };

    // Scene tagger handlers
    const handleGarmentToggle = (garmentId) => {
        const newSelected = new Set(selectedGarments);
        if (newSelected.has(garmentId)) {
            newSelected.delete(garmentId);
        } else {
            newSelected.add(garmentId);
        }
        setSelectedGarments(newSelected);
    };

    const handleSaveGarmentTags = async () => {
        setIsSavingTags(true);
        try {
            const currentGarments = new Set(props.sceneGarments ? props.sceneGarments.map(g => g.id) : []);

            for (const garmentId of currentGarments) {
                if (!selectedGarments.has(garmentId)) {
                    if (taggingType === 'scene') {
                        await props.untagGarmentFromScene(sceneId, garmentId);
                    } else {
                        await props.untagGarmentFromAlbum(albumId, garmentId);
                    }
                }
            }

            for (const garmentId of selectedGarments) {
                if (!currentGarments.has(garmentId)) {
                    if (taggingType === 'scene') {
                        await props.tagGarmentToScene(sceneId, garmentId);
                    } else {
                        await props.tagGarmentToAlbum(albumId, garmentId);
                    }
                }
            }

            setShowGarmentList(false);
            if (onGarmentTagsUpdated) {
                onGarmentTagsUpdated();
            }
        } catch (error) {
            console.error('Error updating garment tags:', error);
        } finally {
            hoverScenesCacheRef.current.clear();
            setIsSavingTags(false);
        }
    };

    const handleDeleteGarment = async (garmentId, actorId) => {
        if (window.confirm('Are you sure you want to delete this garment?')) {
            try {
                await props.deleteGarment(garmentId, actorId);
                
                // Refresh list
                if (selectedActorId) {
                    props.getGarmentsByActorId(selectedActorId);
                } else {
                    props.getAllGarments({ limit: 200, sort_by: 'id', sort_order: 'desc' });
                }
            } catch (error) {
                console.error('Error deleting garment:', error);
            }
        }
    };

    const openEditModal = (garment) => {
        setGarmentMode("Edit");
        setGarmentInfo({ ...garment });
        setShowGarmentEditor(true);
        setFlatLayPrompt('');
        setRegenerateError(null);
        setRegenerateSuccess(false);
        setRegenerateNotice(null);
        
        // Fetch image history for this garment
        if (garment.id) {
            setLoadingImageHistory(true);
            props.getGarmentImageHistory(garment.id)
                .then(images => {
                    setImageHistory(images);
                    setLoadingImageHistory(false);
                })
                .catch(error => {
                    console.error('Error fetching image history:', error);
                    setLoadingImageHistory(false);
                });
        }
    };

    const openCreateModal = () => {
        setGarmentMode("Add");
        resetGarmentForm();
        setShowGarmentEditor(true);
    };

    const showGarmentImageFunc = (imagePath) => {
        setGarmentImage(`${constants.CONTENT_DOMAIN}${imagePath}`);
        setShowGarmentImage(true);
    };

    const hideGarmentImageFunc = () => {
        setShowGarmentImage(false);
    };

    const getResizedCanvas = (canvas, newWidth, newHeight) => {
        const tmpCanvas = document.createElement('canvas');
        tmpCanvas.width = newWidth;
        tmpCanvas.height = newHeight;

        const ctx = tmpCanvas.getContext('2d');
        ctx.drawImage(
            canvas,
            0,
            0,
            canvas.width,
            canvas.height,
            0,
            0,
            newWidth,
            newHeight
        );

        return tmpCanvas;
    };

    const onImageLoad = useCallback((img) => {
        imgRef.current = img;
    }, []);

    useEffect(() => {
        if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
            return;
        }

        const image = imgRef.current;
        const canvas = previewCanvasRef.current;
        const crop = completedCrop;

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const ctx = canvas.getContext('2d');

        canvas.width = Math.ceil(crop.width * pixelRatio);
        canvas.height = Math.ceil(crop.height * pixelRatio);

        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width,
            crop.height
        );
    }, [completedCrop, pixelRatio]);

    const dataURLtoFile = (dataurl, filename) => {
        let arr = dataurl.split(','),
            mime = arr[0].match(/:(.+?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    };

    const generateDownload = (previewCanvas, crop) => {
        if (!crop || !previewCanvas) {
            return null;
        }

        const canvas = getResizedCanvas(previewCanvas, crop.width, crop.height);

        return new Promise((resolve) => {
            const reader = new FileReader();
            canvas.toBlob(blob => {
                reader.readAsDataURL(blob);
                reader.onloadend = () => {
                    const file = dataURLtoFile(reader.result, 'garment-image.webp');
                    resolve(file);
                }
            }, 'image/webp', 0.9);
        });
    };

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        
        // Validate actor is selected
        const actorId = garmentInfo.actor_id || selectedActorId;
        if (!actorId) {
            setUploadError('Please select an actor before uploading an image');
            return;
        }
        
        if (file) {
            // Validate file is an image
            if (!file.type.startsWith('image/')) {
                setUploadError('Please select an image file');
                return;
            }

            // Check file size (max 10MB)
            if (file.size > 10 * 1024 * 1024) {
                setUploadError('File size must be less than 10MB');
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setCropImage(reader.result);
                setUploadError(null);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCropConfirm = async () => {
        try {
            setIsUploadingImage(true);
            setUploadError(null);

            if (!completedCrop?.width || !completedCrop?.height) {
                setUploadError('Please crop the image before uploading');
                return;
            }

            const actorId = garmentInfo.actor_id || selectedActorId;
            if (!actorId) {
                setUploadError('Please select an actor before uploading');
                return;
            }

            const croppedImageFile = await generateDownload(previewCanvasRef.current, completedCrop);

            // Existing garments keep their id; new ones are created by the backend during upload
            const garmentId = garmentInfo.id && garmentInfo.id !== 'new' ? garmentInfo.id : 'new';

            const formData = new FormData();
            formData.append('image', croppedImageFile, 'garment-image.webp');
            if (uploadPrompt.trim()) {
                formData.append('prompt', uploadPrompt.trim());
            }
            if (flatLayModel && flatLayModel !== 'auto') {
                formData.append('flatlay_model', flatLayModel);
            }

            const token = localStorage.getItem('token').replace('Bearer ', '');
            const uploadUrl = `${constants.CONTENT_DOMAIN}upload/garment/${garmentId}/actor/${actorId}?token=${token}`;

            const response = await axios.post(uploadUrl, formData);
            if (!response.data.status) {
                throw new Error(response.data.message || 'Upload failed');
            }

            // The server stores the original, queues the analysis, and writes the flat lay and metadata.
            // Close the prompt now; the queue panel in the list shows progress.
            resetCropState();
            resetGarmentForm();
            setShowGarmentEditor(false);
            fetchGarmentJobs();
            refreshGarmentList();
        } catch (error) {
            console.error('Error uploading image:', error);
            setUploadError(error.response?.data?.message || error.message || 'Error uploading image');
        } finally {
            setIsUploadingImage(false);
        }
    };

    const sortGarments = (key) => {
        const newOrder = sortKey === key && order === "asc" ? "desc" : "asc";
        setOrder(newOrder);
        setSortKey(key);
    };

    // Apply filters and the selected column sort to the garments list
    const baseGarmentsList = selectedActorId ? props.garmentsByActor : props.garments;
    
    const filteredGarmentsList = useMemo(() => {
        const filtered = (baseGarmentsList || []).filter(garment => {
            const actorMatch = !filterActor || (garment.actor_name && garment.actor_name.toLowerCase().includes(filterActor.toLowerCase()));
            const categoryMatch = !filterCategory || (garment.category_name && garment.category_name.toLowerCase().includes(filterCategory.toLowerCase()));
            const taggedMatch = !showTaggedOnly || selectedGarments.has(garment.id);
            const textMatch = !filterText || [
                garment.name, garment.color, garment.actor_name, garment.category_name,
                garment.style_name, garment.pattern_name, garment.texture_name,
                garment.material_name, garment.cut, garment.straps, garment.description
            ].some(field => field && field.toLowerCase().includes(filterText.toLowerCase()));
            return actorMatch && categoryMatch && textMatch && taggedMatch;
        });

        if (!sortKey) {
            return filtered.sort((a, b) => b.id - a.id);
        }
        const direction = order === "desc" ? -1 : 1;
        return filtered.sort((a, b) => compareGarmentValues(a[sortKey], b[sortKey]) * direction);
    }, [baseGarmentsList, filterActor, filterCategory, filterText, showTaggedOnly, selectedGarments, sortKey, order]);

    // Get unique actors and categories for filter dropdowns
    const uniqueActors = useMemo(() => {
        return [...new Set((props.garments || []).map(g => g.actor_name).filter(Boolean))].sort();
    }, [props.garments]);

    const uniqueCategories = useMemo(() => {
        return [...new Set((props.garments || []).map(g => g.category_name).filter(Boolean))].sort();
    }, [props.garments]);

    const garmentsList = filteredGarmentsList;
    const { isFetching, isFetchingAll, isCreating, isUpdating, isDeleting } = props;
    const isLoading = isFetching || isFetchingAll || isCreating || isUpdating || isDeleting;

    const getStoredFlatLayUrl = (garment) => {
        if (garment.flatlay_url) return garment.flatlay_url;
        let attrs = garment.additional_attributes;
        if (typeof attrs === 'string') {
            try { attrs = JSON.parse(attrs); } catch (e) { attrs = null; }
        }
        return attrs?.analysis?.flatLayUrl || null;
    };

    const getFlatLayUrl = (garment) => {
        const stored = getStoredFlatLayUrl(garment);
        if (stored) return stored;
        if (garment.image_url) return garment.image_url.replace('.webp', '_flatlay.webp');
        return null;
    };

    return (
        <>
            {props.show === undefined && (
                <StyledButton
                    variant="info"
                    size={isMobile ? 'sm' : 'md'}
                    onClick={() => handleShow(selectedActorId)}
                >
                    <span>{isTaggerMode ? '👗 Tag Garments' : '👔 Garments'}</span>
                </StyledButton>
            )}

            <ModernModal
                size="xl"
                show={showGarmentList && !showGarmentEditor}
                onHide={handleCloseGarmentList}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{isTaggerMode ? `Tag Garments to Scene #${sceneId}` : 'Wardrobe Management'}
                        {selectedActorId && !isTaggerMode && ` - Actor #${selectedActorId}`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <GenerationQueuePanel jobs={garmentJobs} garments={props.garments} />
                    {/* Tagged Summary (tagger mode only) */}
                    {isTaggerMode && props.sceneGarments && props.sceneGarments.length > 0 && (
                        <TaggedSummaryBar>
                            <span>✅ <strong>{props.sceneGarments.length}</strong> tagged</span>
                            <TaggedChips>
                                {props.sceneGarments.map(g => (
                                    <TaggedChip key={g.id}>
                                        #{g.id} {g.name}
                                        <ChipRemove onClick={() => handleGarmentToggle(g.id)}>✕</ChipRemove>
                                    </TaggedChip>
                                ))}
                            </TaggedChips>
                        </TaggedSummaryBar>
                    )}

                    <Form.Row className="mb-3">
                        <Form.Group as={Col} xs="6" md="4">
                            <Button
                                className="w-100"
                                variant="success"
                                size={isMobile ? 'sm' : 'md'}
                                onClick={openCreateModal}
                            >
                                + Add Garment
                            </Button>
                        </Form.Group>
                        <Form.Group as={Col} xs="6" md="4">
                            <Button
                                className="w-100"
                                variant="secondary"
                                size={isMobile ? 'sm' : 'md'}
                                onClick={() => handleShow(selectedActorId)}
                                disabled={isLoading}
                            >
                                {isLoading ? <Spinner size="sm" /> : '🔄 Refresh'}
                            </Button>
                        </Form.Group>
                    </Form.Row>

                    <Form.Row className="mb-3">
                        <Form.Group as={Col} xs="12" md="4" controlId="managerFilterText">
                            <Form.Label style={{ fontSize: '12px', fontWeight: '600' }}>Search</Form.Label>
                            <Form.Control
                                type="text"
                                size="sm"
                                placeholder="Name, color, description..."
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                            />
                            {garmentTotal !== null && (
                                <div style={{ fontSize: '10px', color: '#888', marginTop: '2px' }}>
                                    Showing {filteredGarmentsList.length} of {garmentTotal} garments
                                </div>
                            )}
                        </Form.Group>
                        <Form.Group as={Col} xs="6" md="4" controlId="filterActor">
                            <Form.Label style={{ fontSize: '12px', fontWeight: '600' }}>Filter by Actor</Form.Label>
                            <Form.Control
                                as="select"
                                size="sm"
                                value={filterActor}
                                onChange={(e) => setFilterActor(e.target.value)}
                            >
                                <option value="">All Actors</option>
                                {uniqueActors.map(actor => (
                                    <option key={actor} value={actor}>{actor}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        <Form.Group as={Col} xs="6" md={isTaggerMode ? '3' : '4'} controlId="filterCategory">
                            <Form.Label style={{ fontSize: '12px', fontWeight: '600' }}>Filter by Category</Form.Label>
                            <Form.Control
                                as="select"
                                size="sm"
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                            >
                                <option value="">All Categories</option>
                                {uniqueCategories.map(category => (
                                    <option key={category} value={category}>{category}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        {isTaggerMode && (
                            <Form.Group as={Col} xs="12" md="2" controlId="taggerShowTagged" style={{ display: 'flex', alignItems: 'flex-end' }}>
                                <Form.Check
                                    type="switch"
                                    id="showTaggedOnly"
                                    label="Tagged only"
                                    checked={showTaggedOnly}
                                    onChange={(e) => setShowTaggedOnly(e.target.checked)}
                                    style={{ fontSize: '12px', fontWeight: '600' }}
                                />
                            </Form.Group>
                        )}
                    </Form.Row>

                    {showGarmentImage && (
                        <GarmentImagePreview
                            src={garmentImage}
                            alt="Garment"
                            onMouseLeave={hideGarmentImageFunc}
                        />
                    )}

                    <GarmentTable striped bordered hover size="sm">
                        <thead>
                            <tr>
                                {isTaggerMode && <th style={{ width: '40px' }}>Tag</th>}
                                <th onClick={() => sortGarments('id')} style={{ cursor: 'pointer' }}>
                                    # <SortIcon>⇅</SortIcon>
                                </th>
                                <th onClick={() => sortGarments('name')} style={{ cursor: 'pointer' }}>
                                    Name <SortIcon>⇅</SortIcon>
                                </th>
                                <th onClick={() => sortGarments('actor_name')} style={{ cursor: 'pointer' }}>
                                    Actor <SortIcon>⇅</SortIcon>
                                </th>
                                <th onClick={() => sortGarments('color')} style={{ cursor: 'pointer' }}>
                                    Color <SortIcon>⇅</SortIcon>
                                </th>
                                <th onClick={() => sortGarments('category_name')} style={{ cursor: 'pointer' }}>
                                    Category <SortIcon>⇅</SortIcon>
                                </th>
                                <th onClick={() => sortGarments('scene_count')} style={{ cursor: 'pointer', textAlign: 'center' }}>
                                    Scenes <SortIcon>⇅</SortIcon>
                                </th>
                                <th>Details</th>
                                <th>Image</th>
                                <th style={{ width: '50px' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {garmentsList && garmentsList.length > 0 ? (
                                garmentsList.map(garment => {
                                    const flatLayUrl = getFlatLayUrl(garment);
                                    return (
                                    <TaggerRow
                                        key={garment.id}
                                        $tagged={isTaggerMode && selectedGarments.has(garment.id)}
                                        onMouseEnter={() => {
                                            if (flatLayUrl) showGarmentImageFunc(flatLayUrl);
                                        }}
                                        onMouseLeave={() => {
                                            hideGarmentImageFunc();
                                        }}
                                    >
                                        {isTaggerMode && (
                                            <td style={{ textAlign: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedGarments.has(garment.id)}
                                                    onChange={() => handleGarmentToggle(garment.id)}
                                                    style={{ width: '16px', height: '16px', accentColor: '#667eea', cursor: 'pointer' }}
                                                />
                                            </td>
                                        )}
                                        <td>{garment.id}</td>
                                        <td onClick={() => isTaggerMode ? handleGarmentToggle(garment.id) : openEditModal(garment)} style={{ cursor: 'pointer' }}>
                                            {garment.name}
                                        </td>
                                        <td>
                                            {garment.actor_name ? (
                                                <ActorBadge>👤 {garment.actor_name}</ActorBadge>
                                            ) : (
                                                <span style={{ color: '#999', fontSize: '11px' }}>N/A</span>
                                            )}
                                        </td>
                                        <td>
                                            {garment.color ? (
                                                garment.color.split(',').map(v => v.trim()).filter(Boolean).map((val, i) => (
                                                    <ColorBadge key={i} color={val} title={val}>
                                                        {val}
                                                    </ColorBadge>
                                                ))
                                            ) : (
                                                <span style={{ color: '#999', fontSize: '11px' }}>—</span>
                                            )}
                                        </td>
                                        <td>
                                            {garment.category_name ? (
                                                <CategoryBadge title={garment.category_name}>
                                                    {garment.category_name.substring(0, 12)}
                                                </CategoryBadge>
                                            ) : (
                                                <span style={{ color: '#999', fontSize: '11px' }}>—</span>
                                            )}
                                        </td>
                                        <td
                                            style={{ textAlign: 'center', cursor: garment.scene_count > 0 ? 'pointer' : 'default' }}
                                            onMouseEnter={() => handleGarmentRowEnter(garment)}
                                            onMouseLeave={scheduleHoverScenesClose}
                                        >
                                            <span style={{ fontWeight: 600, color: garment.scene_count > 0 ? '#333' : '#999' }}>
                                                {garment.scene_count ?? 0}
                                            </span>
                                        </td>
                                        <td>
                                            <CompactDetails>
                                                {garment.cut && garment.cut.split(',').map(v => v.trim()).filter(Boolean).map((val, i) => (
                                                    <DetailBadge key={i} title={val}>{val}</DetailBadge>
                                                ))}
                                            </CompactDetails>
                                        </td>
                                        <td>
                                            {garment.image_url && (
                                                <>
                                                    <ImageThumbnail
                                                        src={`${constants.CONTENT_DOMAIN}${garment.image_url}`}
                                                        alt={garment.name}
                                                        onMouseEnter={() => showGarmentImageFunc(garment.image_url)}
                                                    />
                                                    <ImageThumbnail
                                                        src={`${constants.CONTENT_DOMAIN}${garment.image_url.replace('.webp', '_flatlay.webp')}`}
                                                        alt={`${garment.name} flat lay`}
                                                        onMouseEnter={() => showGarmentImageFunc(garment.image_url.replace('.webp', '_flatlay.webp'))}
                                                        style={{ marginLeft: '4px' }}
                                                    />
                                                </>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                onClick={() => openEditModal(garment)}
                                                title="Edit garment"
                                                style={{ padding: '2px 8px', fontSize: '13px', lineHeight: 1, borderRadius: '4px' }}
                                            >
                                                ✎
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-info"
                                                onClick={() => openScenesModal(garment)}
                                                title="View scenes"
                                                style={{ padding: '2px 8px', fontSize: '13px', lineHeight: 1, borderRadius: '4px', marginLeft: '4px' }}
                                            >
                                                🎬
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                onClick={() => handleDeleteGarment(garment.id, garment.actor_id)}
                                                title="Delete garment"
                                                style={{ padding: '2px 8px', fontSize: '13px', lineHeight: 1, borderRadius: '4px', marginLeft: '4px' }}
                                            >
                                                ✕
                                            </Button>
                                        </td>
                                    </TaggerRow>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={isTaggerMode ? 10 : 9} className="text-center">
                                        {isLoading ? <Spinner size="sm" /> : 'No garments found'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </GarmentTable>

                    {isTaggerMode && (
                        <TagSummary>
                            {selectedGarments.size} garment(s) selected &nbsp;|&nbsp; {garmentsList.length} shown of {(props.garments || []).length} total
                        </TagSummary>
                    )}
                </Modal.Body>
                {isTaggerMode && (
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseGarmentList}>
                            Cancel
                        </Button>
                        <Button
                            variant="success"
                            onClick={handleSaveGarmentTags}
                            disabled={isSavingTags}
                        >
                            {isSavingTags ? <Spinner size="sm" /> : 'Save Tags'}
                        </Button>
                    </Modal.Footer>
                )}
            </ModernModal>

            {/* Edit/Create Garment Modal */}
            <ModernModal
                size="lg"
                show={showGarmentList && showGarmentEditor}
                onHide={handleCancel}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{garmentMode} Garment</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <GenerationQueuePanel jobs={garmentJobs} garments={props.garments} />
                    <Form.Row>
                        <Form.Group as={Col} md="6" controlId="garmentName">
                            <Form.Label>Garment Name *</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter garment name"
                                value={garmentInfo.name}
                                onChange={(e) => setGarmentInfo({ ...garmentInfo, name: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Form.Group as={Col} md="6" controlId="garmentActor">
                            <Form.Label>Actor / Model</Form.Label>
                            <Select
                                name="actor"
                                options={props.allActor || []}
                                values={garmentInfo.actor_id ? [props.allActor?.find(a => a.id === garmentInfo.actor_id)].filter(Boolean) : []}
                                onChange={(values) => setGarmentInfo({ ...garmentInfo, actor_id: values[0]?.id || '' })}
                                labelField="name"
                                valueField="id"
                                placeholder="Select an actor"
                                searchable="true"
                                clearable
                                searchBy="name"
                            />
                        </Form.Group>
                    </Form.Row>

                    <Form.Row>
                        <Form.Group as={Col} md="12" controlId="garmentColor">
                            <Form.Label>Color</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., Red, Blue, Black"
                                value={garmentInfo.color}
                                onChange={(e) => setGarmentInfo({ ...garmentInfo, color: e.target.value })}
                            />
                        </Form.Group>
                    </Form.Row>

                    <Form.Row>
                        <Form.Group as={Col} md="6" controlId="category">
                            <Form.Label>Category</Form.Label>
                            <Form.Control
                                as="select"
                                value={garmentInfo.category_id}
                                onChange={(e) => setGarmentInfo({ ...garmentInfo, category_id: parseInt(e.target.value) })}
                            >
                                <option value="">Select Category</option>
                                {props.attributes.categories && props.attributes.categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        <Form.Group as={Col} md="6" controlId="style">
                            <Form.Label>Style</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., Casual, Formal, Sporty"
                                value={garmentInfo.style}
                                onChange={(e) => setGarmentInfo({ ...garmentInfo, style: e.target.value })}
                            />
                        </Form.Group>
                    </Form.Row>

                    <Form.Row>
                        <Form.Group as={Col} md="6" controlId="pattern">
                            <Form.Label>Pattern</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., Solid, Floral, Striped"
                                value={garmentInfo.pattern}
                                onChange={(e) => setGarmentInfo({ ...garmentInfo, pattern: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group as={Col} md="6" controlId="texture">
                            <Form.Label>Texture</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., Cotton, Silk, Velvet"
                                value={garmentInfo.texture}
                                onChange={(e) => setGarmentInfo({ ...garmentInfo, texture: e.target.value })}
                            />
                        </Form.Group>
                    </Form.Row>

                    <Form.Row>
                        <Form.Group as={Col} md="6" controlId="material">
                            <Form.Label>Fabric/Material</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., Cotton, Polyester, Leather"
                                value={garmentInfo.material}
                                onChange={(e) => setGarmentInfo({ ...garmentInfo, material: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group as={Col} md="6" controlId="cut">
                            <Form.Label>Cut</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., V-neck, Crew neck"
                                value={garmentInfo.cut}
                                onChange={(e) => setGarmentInfo({ ...garmentInfo, cut: e.target.value })}
                            />
                        </Form.Group>
                    </Form.Row>

                    <Form.Row>
                        <Form.Group as={Col} md="6" controlId="straps">
                            <Form.Label>Straps</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., Sleeveless, Long sleeves"
                                value={garmentInfo.straps}
                                onChange={(e) => setGarmentInfo({ ...garmentInfo, straps: e.target.value })}
                            />
                        </Form.Group>
                    </Form.Row>

                    <Form.Row>
                        <Form.Group as={Col} controlId="additionalAttributes">
                            <Form.Label>Additional Attributes</Form.Label>
                            <Form.Control
                                as="textarea"
                                placeholder="e.g., Leather trim, Metal buckles, Lace details"
                                value={
                                    garmentInfo.additional_attributes
                                        ? typeof garmentInfo.additional_attributes === 'string'
                                            ? garmentInfo.additional_attributes.startsWith('{') || garmentInfo.additional_attributes.startsWith('[')
                                                ? JSON.stringify(JSON.parse(garmentInfo.additional_attributes), null, 2)
                                                : garmentInfo.additional_attributes
                                            : JSON.stringify(garmentInfo.additional_attributes, null, 2)
                                        : ''
                                }
                                onChange={(e) => setGarmentInfo({ ...garmentInfo, additional_attributes: e.target.value })}
                                rows="15"
                                style={{
                                    fontFamily: 'Monaco, "Courier New", monospace',
                                    fontSize: '12px',
                                    backgroundColor: '#f8f8f8',
                                    lineHeight: '1.5'
                                }}
                            />
                        </Form.Group>
                    </Form.Row>

                    <Form.Row>
                        <Form.Group as={Col} controlId="description">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                placeholder="Additional notes about this garment"
                                value={garmentInfo.description}
                                onChange={(e) => setGarmentInfo({ ...garmentInfo, description: e.target.value })}
                                rows="2"
                            />
                        </Form.Group>
                    </Form.Row>

                    <Form.Row>
                        <Form.Group as={Col} controlId="garmentImage">
                            <Form.Label>Garment Image</Form.Label>
                            {!garmentInfo.actor_id && !selectedActorId && (
                                <Alert variant="warning" className="mb-3">
                                    ⚠️ Please select an actor first before uploading an image
                                </Alert>
                            )}
                            {uploadError && (
                                <Alert variant="danger" className="mb-3">
                                    {uploadError}
                                </Alert>
                            )}
                            <Form.Control
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                onChange={handleImageUpload}
                                disabled={isUploadingImage || !garmentInfo.actor_id}
                                title={!garmentInfo.actor_id ? "Please select an actor first" : "Select image to upload"}
                            />
                            {cropImage && (
                                <div className="mt-3">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Crop Aspect Ratio</Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={cropRatio}
                                            onChange={(e) => {
                                                const ratio = parseFloat(e.target.value);
                                                setCropRatio(ratio);
                                                setCrop({ ...crop, aspect: ratio });
                                            }}
                                        >
                                            <option value={1}>Square (1:1)</option>
                                            <option value={0.75}>4:3 (Landscape)</option>
                                            <option value={1.33}>3:4 (Portrait)</option>
                                            <option value={1.5}>3:2 (Landscape)</option>
                                            <option value={0.67}>2:3 (Portrait)</option>
                                            <option value={0}>Free Aspect</option>
                                        </Form.Control>
                                    </Form.Group>
                                    <CropContainer>
                                        <ReactCrop
                                            src={cropImage}
                                            onImageLoaded={onImageLoad}
                                            crop={crop}
                                            onChange={(c) => setCrop(c)}
                                            onComplete={(c) => setCompletedCrop(c)}
                                        />
                                    </CropContainer>
                                    <PreviewContainer>
                                        <canvas
                                            ref={previewCanvasRef}
                                            style={{
                                                width: Math.ceil(completedCrop?.width ?? 0),
                                                height: Math.ceil(completedCrop?.height ?? 0)
                                            }}
                                        />
                                    </PreviewContainer>
                                    <Form.Group className="mt-3">
                                        <Form.Label>Prompt <small style={{ color: '#999' }}>(optional — guide the AI flat lay generation)</small></Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows="2"
                                            placeholder="e.g., Show the garment folded neatly, emphasize the lace trim..."
                                            value={uploadPrompt}
                                            onChange={(e) => setUploadPrompt(e.target.value)}
                                            disabled={isUploadingImage}
                                            style={{ fontSize: '13px' }}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mt-2">
                                        <Form.Label>Flat Lay Model</Form.Label>
                                        <Form.Control
                                            as="select"
                                            size="sm"
                                            value={flatLayModel}
                                            onChange={(e) => setFlatLayModel(e.target.value)}
                                            disabled={isUploadingImage}
                                        >
                                            <option value="auto">Auto (best available)</option>
                                            <option value="gpt-4.1">GPT-4.1 (Responses API)</option>
                                            <option value="grok">Grok Imagine (xAI)</option>
                                            <option value="openai">OpenAI (DALL-E / GPT Image)</option>
                                            <option value="gpt-image-2.5-flare">GPT Image 2.5 Flare</option>
                                        </Form.Control>
                                    </Form.Group>
                                    <div className="d-flex justify-content-end mt-3" style={{ gap: '8px' }}>
                                        <Button variant="secondary" onClick={resetCropState} disabled={isUploadingImage}>
                                            Cancel Crop
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleCropConfirm}
                                            disabled={isUploadingImage || !completedCrop?.width}
                                        >
                                            {isUploadingImage ? <Spinner size="sm" /> : 'Crop & Upload'}
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {garmentInfo.image_url && (
                                <>
                                    <div style={{ marginTop: '12px' }}>
                                        <small style={{ color: '#666', fontWeight: 600 }}>Original Upload:</small>
                                        <ImagePreviewSmall src={typeof garmentInfo.image_url === 'string' && garmentInfo.image_url.startsWith('data:') ? garmentInfo.image_url : `${constants.CONTENT_DOMAIN}${garmentInfo.image_url}`} />
                                    </div>
                                    {getStoredFlatLayUrl(garmentInfo) && (
                                        <div style={{ marginTop: '12px' }}>
                                            <small style={{ color: '#666', fontWeight: 600 }}>AI-Generated Flat Lay:</small>
                                            <ImagePreviewSmall src={`${constants.CONTENT_DOMAIN}${getStoredFlatLayUrl(garmentInfo)}`} title="AI-generated flat lay image" />
                                        </div>
                                    )}
                                </>
                            )}
                        </Form.Group>
                    </Form.Row>

                    {/* Regenerate Flat Lay */}
                    {garmentMode === "Edit" && garmentInfo.image_url && (
                        <Form.Row>
                            <Form.Group as={Col} controlId="regenerateFlatLay" style={{ marginTop: '12px' }}>
                                <Form.Label>Regenerate Flat Lay</Form.Label>
                                {regenerateNotice && (
                                    <Alert variant="info" className="mb-2" dismissible onClose={() => setRegenerateNotice(null)}>
                                        {regenerateNotice}
                                    </Alert>
                                )}
                                {regenerateSuccess && (
                                    <Alert variant="success" className="mb-2" dismissible onClose={() => setRegenerateSuccess(false)}>
                                        Flat lay regenerated successfully!
                                    </Alert>
                                )}
                                {regenerateError && (
                                    <Alert variant="danger" className="mb-2" dismissible onClose={() => setRegenerateError(null)}>
                                        {regenerateError}
                                    </Alert>
                                )}
                                <InputGroup>
                                    <Form.Control
                                        as="textarea"
                                        rows="2"
                                        placeholder="Enter a prompt to generate a new flat lay image..."
                                        value={flatLayPrompt}
                                        onChange={(e) => setFlatLayPrompt(e.target.value)}
                                        disabled={isRegeneratingFlatLay}
                                        style={{ fontSize: '13px' }}
                                    />
                                </InputGroup>
                                <Form.Control
                                    as="select"
                                    size="sm"
                                    value={flatLayModel}
                                    onChange={(e) => setFlatLayModel(e.target.value)}
                                    disabled={isRegeneratingFlatLay}
                                    style={{ marginTop: '8px' }}
                                >
                                    <option value="auto">Auto (best available)</option>
                                    <option value="gpt-4.1">GPT-4.1 (Responses API)</option>
                                    <option value="grok">Grok Imagine (xAI)</option>
                                    <option value="openai">OpenAI (DALL-E / GPT Image)</option>
                                    <option value="gpt-image-2.5-flare">GPT Image 2.5 Flare</option>
                                </Form.Control>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleRegenerateFlatLay}
                                    disabled={isRegeneratingFlatLay || !flatLayPrompt.trim()}
                                    style={{ marginTop: '8px' }}
                                >
                                    {isRegeneratingFlatLay ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="mr-1" />
                                            Generating...
                                        </>
                                    ) : 'Regenerate Flat Lay'}
                                </Button>
                            </Form.Group>
                        </Form.Row>
                    )}

                    {/* Image History Gallery */}
                    {garmentMode === "Edit" && (
                        <Form.Row>
                            <Form.Group as={Col} controlId="imageHistory" style={{ marginTop: '20px' }}>
                                <Form.Label>Image History</Form.Label>
                                {loadingImageHistory ? (
                                    <div style={{ padding: '20px', textAlign: 'center' }}>
                                        <Spinner animation="border" size="sm" />
                                    </div>
                                ) : imageHistory.length > 0 ? (
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                                        gap: '10px',
                                        marginTop: '12px'
                                    }}>
                                        {imageHistory.map((img, idx) => (
                                            <div
                                                key={img.id || idx}
                                                onClick={() => {
                                                    setPreviewImage(img);
                                                    setShowImagePreview(true);
                                                }}
                                                style={{
                                                    cursor: 'pointer',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '4px',
                                                    padding: '4px',
                                                    position: 'relative',
                                                    aspectRatio: '1/1'
                                                }}
                                                title={`${img.image_type} - ${new Date(img.created_at).toLocaleDateString()}`}
                                            >
                                                <img
                                                    src={`${constants.CONTENT_DOMAIN}${img.image_url.replace(/^download\//, '')}`}
                                                    alt={`${img.image_type} ${idx}`}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        borderRadius: '2px'
                                                    }}
                                                    // onError={(e) => {
                                                    //     e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23f0f0f0" width="100" height="100"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999" font-size="12"%3ENo image%3C/text%3E%3C/svg%3E';
                                                    // }}
                                                />
                                                <span style={{
                                                    position: 'absolute',
                                                    top: '4px',
                                                    right: '4px',
                                                    background: 'rgba(0, 0, 0, 0.6)',
                                                    color: 'white',
                                                    fontSize: '10px',
                                                    padding: '2px 4px',
                                                    borderRadius: '2px',
                                                    textTransform: 'capitalize'
                                                }}>
                                                    {img.image_type}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                        No image history yet
                                    </div>
                                )}
                            </Form.Group>
                        </Form.Row>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancel}>
                        Cancel
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleSaveGarment}
                        disabled={!garmentInfo.name || isUpdating || isCreating}
                    >
                        {isUpdating || isCreating ? <Spinner size="sm" /> : 'Save'}
                    </Button>
                </Modal.Footer>
            </ModernModal>

            {/* Image History Viewer Modal */}
            <Modal show={showImagePreview} onHide={() => setShowImagePreview(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {previewImage && `${previewImage.image_type} - ${new Date(previewImage.created_at).toLocaleString()}`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ textAlign: 'center', padding: '20px' }}>
                    {previewImage && (
                        <>
                            <img
                                src={`${constants.CONTENT_DOMAIN}${previewImage.image_url.replace(/^download\//, '')}`}
                                alt={`Preview ${previewImage.image_type}`}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '600px',
                                    objectFit: 'contain',
                                    borderRadius: '4px'
                                }}
                                onError={(e) => {
                                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23f0f0f0" width="400" height="400"/%3E%3Ctext x="200" y="200" text-anchor="middle" dy=".3em" fill="%23999" font-size="20"%3EImage not available%3C/text%3E%3C/svg%3E';
                                }}
                            />
                            <div style={{ marginTop: '20px', color: '#666', fontSize: '14px' }}>
                                <div><strong>Type:</strong> {previewImage.image_type}</div>
                                <div><strong>Created:</strong> {new Date(previewImage.created_at).toLocaleString()}</div>
                                {previewImage.file_size && <div><strong>Size:</strong> {(previewImage.file_size / 1024).toFixed(2)} KB</div>}
                                {previewImage.width && previewImage.height && <div><strong>Dimensions:</strong> {previewImage.width}x{previewImage.height}px</div>}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowImagePreview(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Garment Scenes Modal */}
            <Modal show={showScenesModal} onHide={() => setShowScenesModal(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Scenes featuring: {scenesGarment?.name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {scenesLoading ? (
                        <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading scenes...</div>
                    ) : garmentScenes.length === 0 ? (
                        <p className="text-center text-muted py-4">This garment has not been tagged to any scenes.</p>
                    ) : (
                        <>
                            <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                                {garmentScenes.length} scene{garmentScenes.length !== 1 ? 's' : ''}
                            </p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                                {garmentScenes.map(scene => (
                                    <SceneCard key={scene.scene?.id} scene={scene} />
                                ))}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowScenesModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {hoverScenes && createPortal(
                <HoverScenesPanel
                    onMouseEnter={cancelHoverScenesClose}
                    onMouseLeave={scheduleHoverScenesClose}
                >
                    <HoverScenesHeader>
                        🎬 {hoverScenes.garment.name}
                        {!hoverScenes.loading && ` — ${hoverScenes.scenes.length} scene${hoverScenes.scenes.length !== 1 ? 's' : ''}`}
                    </HoverScenesHeader>
                    {hoverScenes.loading ? (
                        <div className="text-center py-3"><Spinner animation="border" size="sm" /> Loading scenes...</div>
                    ) : hoverScenes.scenes.length === 0 ? (
                        <p className="text-center text-muted py-3 mb-0">This garment has not been tagged to any scenes.</p>
                    ) : (
                        hoverScenes.scenes.map(scene => (
                            <div key={scene.scene?.id} style={{ marginBottom: '12px' }}>
                                <SceneCard scene={scene} />
                            </div>
                        ))
                    )}
                </HoverScenesPanel>,
                document.body
            )}
        </>
    );

    function getAttributeName(id, attributeType) {
        if (!id) return 'N/A';
        const attributes = props.attributes[attributeType];
        const attribute = attributes && attributes.find(a => a.id === id);
        return attribute ? attribute.name : 'N/A';
    }
}

// Shows server-side garment generation jobs (analysis and flat-lay regeneration)
function GenerationQueuePanel({ jobs, garments }) {
    if (!jobs || jobs.length === 0) return null;

    const garmentName = (id) => {
        const garment = (garments || []).find(item => String(item.id) === String(id));
        return garment?.name || `Garment #${id}`;
    };

    const active = jobs
        .filter(job => job.status === 'queued' || job.status === 'running')
        .sort((a, b) => (a.queuePosition || 0) - (b.queuePosition || 0));
    const finished = jobs
        .filter(job => job.status === 'completed' || job.status === 'failed')
        .slice(0, 5);

    if (active.length === 0 && finished.length === 0) return null;

    const describeJob = (job) => {
        if (job.status === 'running') return 'Generating…';
        if (job.status === 'queued') return `Waiting · #${job.queuePosition}`;
        if (job.status === 'completed') return 'Done';
        return `Failed: ${job.error || 'unknown error'}`;
    };

    return (
        <QueuePanel>
            <QueueHeader>Generation queue ({active.length} active)</QueueHeader>
            {[...active, ...finished].map(job => (
                <QueueRow key={job.id} $status={job.status}>
                    <QueueName>{garmentName(job.garmentId)}</QueueName>
                    <QueueType>{job.type === 'regenerate' ? 'Flat lay' : 'Analysis'}</QueueType>
                    <QueueState>{describeJob(job)}</QueueState>
                </QueueRow>
            ))}
        </QueuePanel>
    );
}

const QueuePanel = styled.div`
    border: 1px solid #dee2e6;
    border-radius: 6px;
    padding: 8px 12px;
    margin-bottom: 12px;
    background: #f8f9fa;
`;

const QueueHeader = styled.div`
    font-weight: 600;
    font-size: 13px;
    margin-bottom: 6px;
`;

const QueueRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    padding: 3px 0;
    color: ${props => props.$status === 'failed' ? '#dc3545' : props.$status === 'completed' ? '#28a745' : 'inherit'};
`;

const QueueName = styled.span`
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const QueueType = styled.span`
    color: #6c757d;
    min-width: 60px;
`;

const QueueState = styled.span`
    min-width: 130px;
    text-align: right;
`;

// Styled components for tagger mode
const TaggerRow = styled.tr`
    background: ${props => props.$tagged ? 'rgba(40, 167, 69, 0.08) !important' : 'inherit'};
    border-left: ${props => props.$tagged ? '3px solid #28a745' : '3px solid transparent'};

    &:hover {
        background: #f0f2f5 !important;
    }
`;

const HoverScenesPanel = styled.div`
    position: fixed;
    top: 80px;
    right: 24px;
    z-index: 1060;
    width: min(460px, calc(100vw - 48px));
    max-height: calc(100vh - 100px);
    overflow-y: auto;
    padding: 12px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const HoverScenesHeader = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #333;
    margin-bottom: 10px;
`;

const TaggedSummaryBar = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    padding: 10px 14px;
    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
    border: 1px solid #28a745;
    border-radius: 8px;
    font-size: 13px;
`;

const TaggedChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    flex: 1;
`;

const TaggedChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    background: white;
    border: 1px solid #28a745;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    color: #155724;
`;

const ChipRemove = styled.span`
    cursor: pointer;
    color: #dc3545;
    font-weight: 700;
    margin-left: 2px;
    font-size: 12px;

    &:hover {
        color: #a71d2a;
    }
`;

const TagSummary = styled.div`
    margin-top: 16px;
    padding: 12px;
    background: linear-gradient(135deg, #667eea15 0%, #764ba215 100%);
    border-radius: 6px;
    text-align: center;
    font-weight: 600;
    color: #333;
    font-size: 13px;
`;


const mapStateToProps = (state) => {
    return {
        garments: state.garment ? state.garment.garments : [],
        garmentsByActor: state.garment ? state.garment.garmentsByActor : [],
        sceneGarments: state.garment ? state.garment.sceneGarments : [],
        attributes: state.garment ? state.garment.attributes : {
            categories: [],
            styles: [],
            patterns: [],
            textures: [],
            materials: []
        },
        allActor: state.scenes?.actor?.allActor || [],
        isFetching: state.garment ? state.garment.isFetching : false,
        isFetchingAll: state.garment ? state.garment.isFetchingAll : false,
        isCreating: state.garment ? state.garment.isCreating : false,
        isUpdating: state.garment ? state.garment.isUpdating : false,
        isDeleting: state.garment ? state.garment.isDeleting : false,
        error: state.garment ? state.garment.error : null
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        getAllGarments: (params) => dispatch(garmentActions.getAllGarments(params)),
        getGarmentsByActorId: (actorId) => dispatch(garmentActions.getGarmentsByActorId(actorId)),
        getSceneGarments: (sceneId) => dispatch(garmentActions.getSceneGarments(sceneId)),
        getGarmentById: (id) => dispatch(garmentActions.getGarmentById(id)),
        createGarment: (data) => dispatch(garmentActions.createGarment(data)),
        updateGarment: (id, data) => dispatch(garmentActions.updateGarment(id, data)),
        deleteGarment: (id, actorId) => dispatch(garmentActions.deleteGarment(id, actorId)),
        getGarmentAttributes: () => dispatch(garmentActions.getGarmentAttributes()),
        tagGarmentToScene: (sceneId, garmentId) => dispatch(garmentActions.tagGarmentToScene(sceneId, garmentId)),
        untagGarmentFromScene: (sceneId, garmentId) => dispatch(garmentActions.untagGarmentFromScene(sceneId, garmentId)),
        tagGarmentToAlbum: (albumId, garmentId) => dispatch(garmentActions.tagGarmentToAlbum(albumId, garmentId)),
        untagGarmentFromAlbum: (albumId, garmentId) => dispatch(garmentActions.untagGarmentFromAlbum(albumId, garmentId)),
        getGarmentScenes: (garmentId) => dispatch(garmentActions.getGarmentScenes(garmentId)),
        getGarmentImageHistory: (garmentId) => dispatch(garmentActions.getGarmentImageHistory(garmentId)),
        regenerateFlatLay: (garmentId, actorId, prompt, model, attributes) => dispatch(garmentActions.regenerateFlatLay(garmentId, actorId, prompt, model, attributes)),
        getAllActor: () => dispatch(actorActions.getAllActor())
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(GarmentManager);
